import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal } from 'antd';
import { AppstoreOutlined, DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { ApiError, createZone, deleteZone, renameZone } from '../lib/api';
import { normalizeZoneName, zoneNameError } from '../lib/zoneNames';
import { useGuildId } from '../hooks/useGuildId';
import { useProjectAuthority } from '../hooks/useProjectAuthority';
import { useChannelStore } from '../stores/useChannelStore';
import styles from './ZoneManagementDialog.module.css';

type Mode = 'list' | 'create' | 'rename' | 'delete';

interface Props {
  open: boolean;
  onClose: () => void;
  initialZoneId?: string | null;
  mode?: Mode;
}

const mutationError = (error: unknown): MessageDescriptor => {
  if (error instanceof ApiError) {
    switch (error.code) {
      case 'zone.name_conflict':
        return msg`A Zone with this name already exists. Choose a distinct name.`;
      case 'zone.not_empty':
        return msg`This Zone is not empty. Archived channels also prevent deletion. No channels were changed.`;
      case 'zone.not_found':
        return msg`This Zone is no longer available. Refresh Zones to see the current groups.`;
      case 'zone.input_invalid':
        return msg`The Zone name is invalid.`;
    }
    if (error.status === 403)
      return msg`You no longer have permission to manage this Project’s Zones.`;
    if (error.status === 404)
      return msg`This Zone is no longer available. Refresh Zones to see the current groups.`;
  }
  return msg`Could not save this change. Please try again.`;
};

export const ZoneManagementDialog = ({
  open,
  onClose,
  initialZoneId = null,
  mode = 'list',
}: Props) => {
  const guildId = useGuildId();
  const authority = useProjectAuthority(guildId);
  const canManageZones = authority.ready && authority.canManageProject;
  useEffect(() => {
    if (open && !canManageZones) onClose();
  }, [canManageZones, onClose, open]);
  if (!open || !canManageZones) return null;
  return (
    <ZoneDialog
      key={`${guildId}:${mode}:${initialZoneId}`}
      guildId={guildId}
      initialZoneId={initialZoneId}
      initialMode={mode}
      onClose={onClose}
    />
  );
};

const ZoneDialog = ({
  guildId,
  initialZoneId,
  initialMode,
  onClose,
}: {
  guildId: string;
  initialZoneId: string | null;
  initialMode: Mode;
  onClose: () => void;
}) => {
  const { t, i18n } = useLingui();
  const zones = useChannelStore((state) => state.zones);
  const loading = useChannelStore((state) => state.loading);
  const loadError = useChannelStore((state) => state.loadError);
  const invalidateChannels = useChannelStore((state) => state.invalidateChannels);
  const [mode, setMode] = useState(initialMode);
  const [zoneId, setZoneId] = useState(initialZoneId);
  const [name, setName] = useState(
    () => zones.find((zone) => zone.id === initialZoneId)?.name ?? '',
  );
  const [selectedName, setSelectedName] = useState(name);
  const [error, setError] = useState<MessageDescriptor | null>(null);
  const [busy, setBusy] = useState(false);
  const [committed, setCommitted] = useState(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const unavailable =
    (mode === 'rename' || mode === 'delete') && !zones.some((zone) => zone.id === zoneId);
  const begin = (nextMode: Mode, id: string | null = null) => {
    const nextName = zones.find((zone) => zone.id === id)?.name ?? '';
    setMode(nextMode);
    setZoneId(id);
    setName(nextName);
    setSelectedName(nextName);
    setError(null);
    setCommitted(false);
  };

  const refresh = async () => {
    setBusy(true);
    try {
      await invalidateChannels(guildId);
      if (mounted.current && committed) onClose();
    } catch {
      if (mounted.current)
        setError(
          committed
            ? msg`Your change was saved, but Zones could not be refreshed. Refresh Zones to load the current state.`
            : msg`Could not refresh Zones. Please try again.`,
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const submit = async () => {
    if (busy || committed || unavailable || loading || loadError || mode === 'list') return;
    if (mode !== 'delete') {
      const invalid = zoneNameError(name);
      if (invalid) {
        setError(invalid);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      if (mode === 'create') await createZone(guildId, normalizeZoneName(name));
      else if (mode === 'rename' && zoneId !== null)
        await renameZone(guildId, zoneId, normalizeZoneName(name));
      else if (mode === 'delete' && zoneId !== null) await deleteZone(guildId, zoneId);
      else throw new Error('A named Zone is required.');
    } catch (failure) {
      if (mounted.current) {
        setError(mutationError(failure));
        setBusy(false);
      }
      return;
    }
    if (!mounted.current) return;
    setCommitted(true);
    try {
      await invalidateChannels(guildId);
      if (mounted.current) onClose();
    } catch {
      if (mounted.current)
        setError(
          msg`Your change was saved, but Zones could not be refreshed. Refresh Zones to load the current state.`,
        );
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const title =
    mode === 'list'
      ? t`Zones`
      : mode === 'create'
        ? t`Create Zone`
        : mode === 'rename'
          ? t`Rename Zone`
          : t`Delete Zone?`;
  const errorText =
    (error ? i18n._(error) : '') ||
    (unavailable
      ? t`This Zone is no longer available. Refresh Zones to see the current groups.`
      : '') ||
    (loadError ? t`Could not load Zones. Refresh Zones to load the current groups.` : '');
  return (
    <Modal
      open
      centered
      title={title}
      onCancel={() => !busy && onClose()}
      width={480}
      className={styles.dialog}
      footer={null}
      closable={!busy}
      mask={{ closable: !busy }}
      keyboard={!busy}
      destroyOnHidden
    >
      <div className={styles.body}>
        {mode === 'list' ? (
          <>
            <p className={styles.description}>
              <Trans>
                Named groups for this Project’s channels. Channel permissions are managed on each
                channel.
              </Trans>
            </p>
            {!loading && !loadError && (
              <div className={styles.list}>
                <div className={styles.row}>
                  <div className={styles.name}>
                    <Trans>Default (no Zone)</Trans>
                    <small>
                      <Trans>Virtual · no Zone record</Trans>
                    </small>
                  </div>
                  <span className={styles.hint}>
                    <Trans>Built-in</Trans>
                  </span>
                </div>
                {[...zones]
                  .sort(
                    (a, b) =>
                      a.name.localeCompare(b.name, undefined, { numeric: true }) ||
                      a.id.localeCompare(b.id),
                  )
                  .map((zone) => (
                    <div key={zone.id} className={styles.row}>
                      <AppstoreOutlined aria-hidden />
                      <div className={styles.name}>
                        {zone.name}
                        <small>
                          <Trans>Named Zone</Trans>
                        </small>
                      </div>
                      <Button
                        type="text"
                        icon={<EditOutlined aria-hidden="true" />}
                        aria-label={t`Rename Zone: ${zone.name}`}
                        onClick={() => begin('rename', zone.id)}
                      />
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined aria-hidden="true" />}
                        aria-label={t`Delete Zone: ${zone.name}`}
                        onClick={() => begin('delete', zone.id)}
                      />
                    </div>
                  ))}
              </div>
            )}
            <Button
              icon={<PlusOutlined aria-hidden="true" />}
              onClick={() => begin('create')}
              disabled={loading || !!loadError}
            >
              <Trans>Create Zone</Trans>
            </Button>
          </>
        ) : mode === 'delete' ? (
          <>
            <p className={styles.description}>
              <Trans>
                Delete <strong>{selectedName}</strong> from this Project. Only an empty Zone can be
                deleted.
              </Trans>
            </p>
            <p className={styles.hint}>
              <Trans>
                Archived channels also prevent deletion. No channel will be deleted or reassigned.
              </Trans>
            </p>
          </>
        ) : (
          <>
            <p className={styles.description}>
              {mode === 'create'
                ? t`Group related channels in the sidebar. A Zone does not change channel access or add members.`
                : t`The name changes in the sidebar and channel breadcrumb. Channel links and access stay the same.`}
            </p>
            <form
              id="zone-name-form"
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
              className={styles.field}
            >
              <label htmlFor="zone-name">
                <Trans>Zone name</Trans>
              </label>
              <Input
                id="zone-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setError(null);
                }}
                disabled={busy || committed}
                autoComplete="off"
                autoFocus
                placeholder={t`e.g. Customer experience`}
                aria-describedby="zone-name-hint"
                aria-invalid={!!errorText}
              />
              <p id="zone-name-hint" className={styles.hint}>
                <Trans>
                  1–100 characters. Names must be distinct within this Project, ignoring case.
                </Trans>
              </p>
            </form>
            {mode === 'create' && (
              <p className={styles.hint}>
                <Trans>Project members can see the Zone, including when it has no channels.</Trans>
              </p>
            )}
          </>
        )}
        {loading && (
          <p role="status">
            <Trans>Loading Zones…</Trans>
          </p>
        )}
        {errorText && (
          <div role="alert" className={styles.error}>
            <p>{errorText}</p>
            <Button size="small" onClick={() => void refresh()} loading={busy}>
              <Trans>Refresh Zones</Trans>
            </Button>
          </div>
        )}
        {mode !== 'list' && (
          <div className={styles.footer}>
            <Button onClick={onClose} disabled={busy}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="primary"
              danger={mode === 'delete'}
              loading={busy}
              disabled={committed || unavailable || loading || !!loadError}
              onClick={() => void submit()}
            >
              {mode === 'create'
                ? t`Create Zone`
                : mode === 'rename'
                  ? t`Save name`
                  : t`Delete Zone`}
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
